/**
 * Feature Flag Registry
 *
 * Central registry of all feature flags in the application.
 * Add new features here to make them available across the app.
 */

import { FeatureFlagRegistry } from "./types";

export const FEATURE_FLAGS: FeatureFlagRegistry = {
  /**
   * AI-powered custom columns
   * Allows users to use AI models to generate column values
   */
  AI_COLUMNS: {
    key: "ai-columns",
    name: "AI-powered Columns",
    description: "Enable AI model picker for custom columns",
    defaultValue: false,
    requiresEntitlement: true,
  },

  /**
   * Formula custom fields
   * Adds the Formula type to the custom-field pickers. Values are computed and
   * materialized by the backend, so the columns render read-only.
   */
  FORMULA_FIELDS: {
    key: "formula-fields",
    name: "Formula Fields",
    description: "Enable formula custom fields (backend-evaluated, read-only values)",
    defaultValue: false,
  },

  /**
   * Build company list feature
   * Allows users to build and manage company lists
   */
  BUILD_COMPANY_LIST: {
    key: "build-company-list",
    name: "Build Company List",
    description: "Enable company list building feature",
    defaultValue: true,
  },

  /**
   * Advanced filtering
   * Enables advanced filter options in list views
   */
  ADVANCED_FILTERING: {
    key: "advanced-filtering",
    name: "Advanced Filtering",
    description: "Enable advanced filter options",
    defaultValue: true,
  },

  /**
   * Bulk operations
   * Enables bulk edit, delete, and update operations
   */
  BULK_OPERATIONS: {
    key: "bulk-operations",
    name: "Bulk Operations",
    description: "Enable bulk edit, delete, and update operations",
    defaultValue: true,
  },

  /**
   * Workspace business address
   * Enables business address field in workspace setup (not yet supported by backend)
   */
  WORKSPACE_BUSINESS_ADDRESS: {
    key: "workspace-business-address",
    name: "Workspace Business Address",
    description: "Enable business address field in workspace setup",
    defaultValue: false,
  },

  /**
   * Workspace phone number
   * Enables business phone field in workspace settings (not yet supported by backend)
   */
  WORKSPACE_PHONE: {
    key: "workspace-phone",
    name: "Workspace Phone Number",
    description: "Enable business phone field in workspace settings",
    defaultValue: false,
  },

  /**
   * Workspace website
   * Enables business website field in workspace settings (not yet supported by backend)
   */
  WORKSPACE_WEBSITE: {
    key: "workspace-website",
    name: "Workspace Website",
    description: "Enable business website field in workspace settings",
    defaultValue: false,
  },

  /**
   * Workspace billing email
   * Enables billing email field in workspace settings (not yet supported by backend)
   */
  WORKSPACE_BILLING_EMAIL: {
    key: "workspace-billing-email",
    name: "Workspace Billing Email",
    description: "Enable billing email field in workspace settings",
    defaultValue: false,
  },

  /**
   * User scheduling link
   * Enables scheduling link field in profile settings (not yet supported by backend)
   */
  USER_SCHEDULING_LINK: {
    key: "user-scheduling-link",
    name: "User Scheduling Link",
    description: "Enable scheduling link field in profile settings",
    defaultValue: false,
  },

  /**
   * User email signature
   * Enables email signature field in profile settings
   */
  USER_EMAIL_SIGNATURE: {
    key: "user-email-signature",
    name: "User Email Signature",
    description: "Enable email signature field in profile settings",
    defaultValue: true,
  },

  /**
   * Setup business type step
   * Enables business type selection in setup flow (not yet supported by backend)
   */
  SETUP_BUSINESS_TYPE: {
    key: "setup-business-type",
    name: "Setup Business Type",
    description: "Enable business type selection step in setup flow",
    defaultValue: true,
  },

  /**
   * Setup apps/integrations step
   * Enables apps and integrations configuration in setup flow (not yet supported by backend)
   */
  SETUP_APPS: {
    key: "setup-apps",
    name: "Setup Apps & Integrations",
    description: "Enable apps and integrations step in setup flow",
    defaultValue: true,
  },

  /**
   * Notifications bell
   *
   * Gates the whole feed: AppLayout renders NotificationBell only when this is on, so with
   * it off the hooks do not exist and nothing fetches. That matters because AppLayout is
   * always mounted — see docs/COMMON_BUGS.md on always-mounted components polling.
   *
   * On for everyone: the feed backend is deployed wherever the app is. A workspace can still
   * opt out by setting `notifications: false` in workspace_preferences, which wins over this
   * default.
   */
  NOTIFICATIONS: {
    key: "notifications",
    name: "Notifications",
    description: "Enable notifications bell in app header",
    defaultValue: true,
  },

  /**
   * Favorites menu
   * Displays favorites menu item in sidebar (not yet supported by backend)
   */
  FAVORITES: {
    key: "favorites",
    name: "Favorites",
    description: "Enable favorites menu item in sidebar",
    defaultValue: true,
  },

  /**
   * Agents menu
   * Displays the Agents page (Settings → Agents), backed by the agentTemplates /
   * agentDefinitions GraphQL API.
   *
   * Default-ON: the feature is going to general availability. It was default-off pending a
   * per-workspace opt-in, which meant every customer needed an operator to edit prefs before
   * they could see Agents at all — the same per-customer operational step that
   * `rules_engine_enabled` was flipped on to remove.
   *
   * READ BEFORE ASSUMING THIS IS SAFE IN A GIVEN ENV: there is no per-env default, so `true`
   * ships EVERYWHERE the build goes — stg on the next build, prd on the next prd promotion.
   * The gate on "only after staging passes" is the promotion decision, not this flag. It also
   * assumes the agents backend (the `agent_definition` table and the BYOA API) is deployed in
   * the target env; where it is not, the page loads against an API that cannot serve it. An
   * explicit per-workspace value still wins, so a workspace can be switched off via Coffee Bar
   * without a deploy.
   */
  AGENTS: {
    key: "agents",
    name: "Agents",
    description: "Enable the Agents page and sidebar menu item",
    defaultValue: true,
  },


  /**
   * Ask Coffee AI assistant
   * Displays Ask Coffee menu item in sidebar (not yet supported by backend)
   */
  ASK_COFFEE: {
    key: "ask-coffee",
    name: "Ask Coffee",
    description: "Enable Ask Coffee AI assistant menu item in sidebar",
    defaultValue: true,
  },

  /**
   * Workspace integrations settings
   * Displays integrations section in workspace settings (not yet supported by backend)
   */
  WORKSPACE_INTEGRATIONS: {
    key: "workspace-integrations",
    name: "Workspace Integrations",
    description: "Enable integrations section in workspace settings",
    defaultValue: true,
  },

  /**
   * MCP connections
   * The "Custom MCP server" connect card, its connected cards and the confirmation dialog, inside
   * the Integrations tab. Gated separately from WORKSPACE_INTEGRATIONS (which gates the tab
   * itself) because the backend half ships to stg first: the tab stays useful while this is off.
   * Defaults OFF — the section is dead weight until coffee #4598 is deployed to the environment.
   */
  MCP_CONNECTIONS: {
    key: "mcp-connections",
    name: "MCP Connections",
    description: "Enable connecting custom MCP servers from workspace integrations settings",
    defaultValue: false,
  },

  /**
   * Workspace data sources (MCP-style catalog)
   * Curated catalog of external systems Coffee can ingest data from
   * (Intercom, PostHog, Zendesk, etc.) via MCP. Frontend-only preview.
   */
  WORKSPACE_DATA_SOURCES: {
    key: "workspace-data-sources",
    name: "Workspace Data Sources",
    description: "Enable the Data Sources catalog in workspace settings (MCP-based ingestion)",
    defaultValue: true,
  },

  /**
   * Workspace intelligence settings
   * Displays intelligence section in workspace settings with backend integration
   */
  WORKSPACE_INTELLIGENCE: {
    key: "workspace-intelligence",
    name: "Workspace Intelligence",
    description: "Enable intelligence section in workspace settings",
    defaultValue: true,
  },

  // PRIVACY_MEETING_VISIBILITY was removed: the unwired "Notes & Recordings
  // Visibility" mock it gated is superseded by the Sharing & Visibility
  // section's per-entity default ACLs (RECORD_SHARING).

  /**
   * Privacy - Excluded email addresses
   * Displays excluded email addresses field in privacy settings (not yet supported by backend)
   */
  PRIVACY_EXCLUDED_EMAILS: {
    key: "privacy-excluded-emails",
    name: "Privacy Excluded Emails",
    description: "Enable excluded email addresses field in privacy section",
    defaultValue: false,
  },

  /**
   * Accounts - Backsync Range section
   * Displays the backsync range settings in accounts section (not yet supported by backend)
   */
  ACCOUNTS_BACKSYNC_RANGE: {
    key: "accounts-backsync-range",
    name: "Accounts Backsync Range",
    description: "Enable backsync range settings in accounts section",
    defaultValue: false,
  },

  /**
   * Call Recorder External Integration
   * Enables "Integrate with another recorder" option (not yet supported by backend)
   */
  CALL_RECORDER_EXTERNAL_INTEGRATION: {
    key: "call-recorder-external-integration",
    name: "Call Recorder External Integration",
    description: "Enable integration with external call recorders",
    defaultValue: false,
  },

  /**
   * Fireflies Recorder
   * Gates every Fireflies.ai entry point in Settings (recorder dropdown, connect /
   * sync / disconnect / webhook cards, the Integrations card). ANDed with the
   * `fireflies_enabled` workspace opt-out. Meetings already imported from
   * Fireflies stay visible either way.
   */
  FIREFLIES_RECORDER: {
    key: "fireflies-recorder",
    name: "Fireflies Recorder",
    description: "Connect Fireflies.ai as an external call recorder (personal + workspace)",
    defaultValue: false,
  },

  /**
   * Test notification styles
   * Enables test buttons for preview notification toasts (not yet needed for production)
   */
  NOTIFICATIONS_TEST_STYLES: {
    key: "notifications-test-styles",
    name: "Test Notification Styles",
    description: "Enable test buttons for previewing notification toast styles",
    defaultValue: false,
  },

  /**
   * Past meetings view
   * Enables "Past" button in Today page (backend sorting not supported)
   */
  PAST_MEETINGS_VIEW: {
    key: "past-meetings-view",
    name: "Past Meetings View",
    description: "Enable Past meetings view button in Today page",
    defaultValue: true,
  },

  /**
   * Briefing recent changes tab
   * Enables "Recent Changes" tab in meeting briefing sheet (not yet supported by backend)
   */
  BRIEFING_RECENT_CHANGES: {
    key: "briefing-recent-changes",
    name: "Briefing Recent Changes",
    description: "Enable Recent Changes tab in meeting briefing sheet",
    defaultValue: false,
  },

  /**
   * Meeting intelligence viewed by section
   * Displays "Viewed By" section showing who has viewed the meeting recording
   */
  MEETING_INTELLIGENCE_VIEWED_BY: {
    key: "meeting-intelligence-viewed-by",
    name: "Meeting Intelligence Viewed By",
    description: 'Display "Viewed By" section in meeting intelligence sheet',
    defaultValue: false,
  },

  /**
   * Today page leads section
   * Displays Leads tab on Today page
   */
  TODAY_LEADS: {
    key: "today-leads",
    name: "Today Page Leads",
    description: "Enable Leads tab on Today page",
    defaultValue: false,
  },

  /**
   * Sidebar add button
   * Displays + button in sidebar header for adding new items (not yet fully implemented)
   */
  SIDEBAR_ADD_BUTTON: {
    key: "sidebar-add-button",
    name: "Sidebar Add Button",
    description: "Enable + button in sidebar header",
    defaultValue: true,
  },

  // NOTES_PRIVACY_SETTINGS was removed: the mock per-row privacy picker it
  // gated is superseded by the real share dialog (RECORD_SHARING + the
  // workspace's recordSharingEnabled preference).

  /**
   * Notes summary row
   * Displays summary statistics row in notes table (backend support pending)
   */
  NOTES_SUMMARY_ROW: {
    key: "notes-summary-row",
    name: "Notes Summary Row",
    description: "Enable summary row with statistics in notes table",
    defaultValue: false,
  },

  /**
   * Today page Ask Coffee search bar
   * Displays "Ask Coffee a question or search..." textbox on Today page
   */
  TODAY_ASK_COFFEE_BAR: {
    key: "today-ask-coffee-bar",
    name: "Today Page Ask Coffee Bar",
    description: "Enable Ask Coffee search bar on Today page",
    defaultValue: false,
  },

  /**
   * Rename column functionality
   * Enables "Rename Column" option in table headers (not yet supported by backend)
   */
  RENAME_COLUMN: {
    key: "rename-column",
    name: "Rename Column",
    description: "Enable rename column functionality in table headers",
    defaultValue: false,
  },

  /**
   * Delete and change type for custom columns
   * Enables "Delete Column" and "Change Type" options for custom columns (not yet supported by backend)
   */
  DELETE_AND_CHANGE_CUSTOM_COLUMNS: {
    key: "delete-and-change-custom-columns",
    name: "Delete and Change Custom Columns",
    description:
      "Enable delete column and change type functionality for custom columns",
    defaultValue: false,
  },

  /**
   * Delete lists functionality
   * Enables "Delete List" option in list dropdown menu (uses updateUserList with hidden: true)
   */
  DELETE_LISTS: {
    key: "delete-lists",
    name: "Delete Lists",
    description: "Enable delete list functionality in list dropdown menus",
    defaultValue: false,
  },

  /**
   * Deals page billing column
   * Displays the Billing column in the deals table (not yet implemented)
   */
  DEALS_BILLING_COLUMN: {
    key: "deals-billing-column",
    name: "Deals Billing Column",
    description: "Show the Billing column in the deals table",
    defaultValue: false,
  },

  /**
   * Deal detail sheet activity tab
   * Activity tracking for deals is not yet supported by backend
   */
  DEAL_ACTIVITY_TAB: {
    key: "deal-activity-tab",
    name: "Deal Activity Tab",
    description:
      "Show the Activity tab in the deal detail sheet (not yet supported by backend)",
    defaultValue: false,
  },

  /**
   * Multi-value phone numbers on person/company detail sheets
   * Enables adding multiple phone numbers per contact/account, mirroring the
   * email/website multi-value pattern. Backend does not yet persist additional
   * phones, so extras are UI-only for now.
   */
  MULTI_VALUE_PHONE: {
    key: "multi-value-phone",
    name: "Multi-value Phone Numbers",
    description:
      "Enable adding multiple phone numbers on person and company detail sheets (frontend only until backend support lands)",
    defaultValue: false,
  },

  /**
   * Sync interval configuration
   * Enables sync interval dropdown in sync wizards (not yet supported by backend)
   */
  SYNC_INTERVAL: {
    key: "sync-interval",
    name: "Sync Interval",
    description: "Enable sync interval configuration in sync wizards (not yet supported by backend)",
    defaultValue: false,
  },

  /**
   * Stage/pipeline selection step
   * Enables the pipeline/stage selection step in CRM sync wizards
   */
  STAGE_SELECTION: {
    key: "stage-selection",
    name: "Stage Selection",
    description: "Enable pipeline/stage selection step in CRM sync wizards",
    defaultValue: true,
  },

  /**
   * Visitor ID
   * Show the Visitor ID settings tab and enable visitor tracking features
   */
  VISITOR_ID: {
    key: "visitor-id",
    name: "Visitor ID",
    description: "Enable Visitor ID settings tab for website visitor identification and Slack notifications",
    defaultValue: true,
  },

  /**
   * Visitor ID Slack notification filters
   * Adds ICP and buyer-persona toggles under the Slack channel selector
   */
  VISITOR_ID_SLACK_FILTERS: {
    key: "visitor-id-slack-filters",
    name: "Visitor ID Slack Filters",
    description: "Enable ICP and buyer-persona filter toggles for Visitor ID Slack notifications",
    defaultValue: true,
  },

  /**
   * Visitor ID target-account filter
   *
   * Gates the "Target accounts" section in Visitor ID settings: pick a company list and
   * visitors from companies outside it are dropped at ingestion by visitor-id-service —
   * not recorded, not added to lists, not notified about.
   *
   * Off by default because the service side is not deployed yet. With the flag on but an
   * old visitor-id-service running, the setting saves and does nothing, which reads as a
   * broken filter. Flip the default once the filter is live in production.
   */
  VISITOR_ID_TARGET_ACCOUNTS: {
    key: "visitor-id-target-accounts",
    name: "Visitor ID Target Accounts",
    description: "Enable the target-account company list filter for Visitor ID",
    defaultValue: false,
  },

  /**
   * Visitor ID ICP / buyer-persona match filters
   *
   * Gates the "Match filters" section in Visitor ID settings. visitor-id-service drops
   * visitors outside the workspace ICP or buyer persona at ingestion — not recorded, not
   * added to lists, not notified about. Separate from VISITOR_ID_SLACK_FILTERS, which
   * defaults on and dates from when these toggles were notification-only and inert.
   *
   * Off by default for the same reason as VISITOR_ID_TARGET_ACCOUNTS: with an old
   * visitor-id-service or icp-builder running, the toggles save and do nothing. Flip the
   * default once both services carry the filter in production.
   */
  VISITOR_ID_MATCH_FILTERS: {
    key: "visitor-id-match-filters",
    name: "Visitor ID Match Filters",
    description: "Enable the ICP and buyer-persona match filters for Visitor ID",
    defaultValue: false,
  },

  /**
   * Visitor ID email notifications
   * Adds an email-notification toggle + recipient input under the Notifications section
   */
  VISITOR_ID_EMAIL_NOTIFICATIONS: {
    key: "visitor-id-email-notifications",
    name: "Visitor ID Email Notifications",
    description: "Enable email-delivery toggle and recipient input for Visitor ID notifications",
    defaultValue: true,
  },

  /**
   * Workspace Rules ("Guardrails & automations")
   * Enables the deterministic rules engine — trigger→condition→action automations and
   * sync guardrails that block a stage move. The UI now lives on the Agents page (the
   * "Build" box routes rule-like prompts to it); this flag gates that section and the
   * rule branch of the router. Formerly the standalone "Recipes" Settings section.
   *
   * Default-ON, tracked with AGENTS. Leaving it off while AGENTS is on is not neutral: the NL
   * router classifies rule-like prompts deliberately (100% guardrail recall on the
   * intent-classifier eval) and then has nowhere to send them, so it silently drafts an agent
   * instead and toasts "ask an admin to add the rule" — a dead end reached on purpose. The
   * section is admin-gated regardless (`rulesAvailable = showRules && isAdminOrOwner`).
   *
   * NOTE THE COUPLING, because this flag reads as cosmetic and is not: `rules_engine_enabled`
   * defaults ON server-side, so revealing this section makes self-serve rule authoring
   * immediately live-executing for any admin who can see it. Showing the UI IS enabling the
   * behavior. Execution remains separately killable per workspace via that preference.
   */
  WORKSPACE_RULES: {
    key: "workspace-rules",
    name: "Workspace Rules",
    description: "Show the Guardrails & automations (rules engine) section on the Agents page. Rule execution is separately gated by the backend rules_engine_enabled workspace preference.",
    defaultValue: true,
  },

  /**
   * Deal stage required fields
   * The per-stage "fields that must be filled out to move into this stage" editor in
   * Settings → Deals → Pipelines. Enforced: writes sync guard `automation_rule` rows
   * (require_fields) that block the move server-side.
   */
  STAGE_ENTRY_CRITERIA: {
    key: "stage-entry-criteria",
    name: "Deal Stage Required Fields",
    description: "Per-stage required-fields editor (enforced via sync guard rules) in pipeline settings",
    defaultValue: false,
  },

  /**
   * Deal stage free-text criteria notes (design/preview only)
   * The free-text "Entry criteria" note + off/suggest/auto automation block in the same
   * panel. NOT enforced and has no backend consumer — kept for Lovable preview / Doug's
   * design pass, gated separately so it isn't exposed to real workspaces (e.g. Alchemy).
   */
  STAGE_ENTRY_CRITERIA_NOTES: {
    key: "stage-entry-criteria-notes",
    name: "Deal Stage Criteria Notes (preview)",
    description: "Free-text per-stage entry-criteria notes + automation mode. Not enforced; preview/design only.",
    defaultValue: false,
  },

  /**
   * Proposal & Invoice agents
   * Show the Proposal Agent and Invoice Agent cards in the Agents section
   */
  AGENTS_PROPOSAL_INVOICE: {
    key: "agents-proposal-invoice",
    name: "Proposal & Invoice Agents",
    description: "Show Proposal Agent and Invoice Agent cards in the Agents section",
    defaultValue: false,
  },

  /**
   * Notifications - Web delivery method
   *
   * Gates the browser-push row in personal notification settings. Off by default because
   * push needs three things live before the toggle can do anything: VITE_VAPID_PUBLIC_KEY
   * in the build, the web_push secret in that environment, and the /sw.js route deployed.
   * With any of them missing the toggle degrades honestly (the hook reports `unconfigured`)
   * but offers the user something that cannot work.
   *
   * The in-app bell is NOT gated by this — it is unconditional. This is push only.
   */
  NOTIFICATIONS_WEB_DELIVERY: {
    key: "notifications-web-delivery",
    name: "Notifications Web Delivery",
    description: "Show Web delivery option alongside Email and Slack in notification settings",
    defaultValue: false,
  },


  /**
   * CRM custom field creation
   * Enables "Create Custom Field" option in CRM sync mapping dropdowns (not yet supported by backend)
   */
  CRM_CUSTOM_FIELD_CREATION: {
    key: "crm-custom-field-creation",
    name: "CRM Custom Field Creation",
    description: "Enable custom field creation in CRM sync mapping dropdowns",
    defaultValue: true,
  },

  /**
   * Lead Finder (ICP Builder)
   * Displays Lead Finder menu item in sidebar — gates access to /list-builder
   */
  LEAD_FINDER: {
    key: "lead-finder",
    name: "Lead Finder",
    description: "Enable Lead Finder menu item in sidebar",
    defaultValue: true,
  },

  /**
   * Lead Finder people → company criteria
   * Shows company filter fields (industry, employees, revenue, etc.) in people search
   */
  LEAD_FINDER_PEOPLE_COMPANY_CRITERIA: {
    key: "lead-finder-people-company-criteria",
    name: "Lead Finder People Company Criteria",
    description: "Show company criteria fields in people search on Lead Finder",
    defaultValue: false,
  },

  /**
   * Manual file upload
   * Allows users to manually upload files from drill-down detail views
   */
  MANUAL_FILE_UPLOAD: {
    key: "manual-file-upload",
    name: "Manual File Upload",
    description: "Enable manual file upload button on Files tabs in drill-down views",
    defaultValue: true,
  },

  /**
   * Billing credit usage page
   * Shows credit usage summary and per-agent breakdown before navigating to Stripe
   */
  BILLING_CREDIT_USAGE: {
    key: "billing-credit-usage",
    name: "Billing Credit Usage",
    description: "Enable credit usage summary page in billing section",
    defaultValue: false,
  },

  /**
   * Drill-down sheet actions menu
   * Displays actions dropdown (Add to List, Send Email, Delete) in detail sheet headers
   */
  DRILL_DOWN_ACTIONS: {
    key: "drill-down-actions",
    name: "Drill-Down Actions",
    description: "Enable actions dropdown menu in detail sheet headers",
    defaultValue: true,
  },
  /**
   * Extended company filters
   * Enables filters not yet in coffee-ui reference: continent, linkedin, description, founding year, create date
   */
  EXTENDED_COMPANY_FILTERS: {
    key: "extended-company-filters",
    name: "Extended Company Filters",
    description: "Enable additional company filters not yet in coffee-ui (continent, linkedin, description, founding year, create date)",
    defaultValue: false,
  },

  /**
   * Meeting company edit
   * Enables pencil icon to reassign a meeting's associated company (not yet wired to backend)
   */
  MEETING_COMPANY_EDIT: {
    key: "meeting-company-edit",
    name: "Meeting Company Edit",
    description: "Enable company reassignment in meeting intelligence sheet (not yet wired to backend)",
    defaultValue: false,
  },

  /**
   * Reports
   * Enables conversational report building in Ask Coffee, the "Build a report"
   * affordance, the Reports tab in Favorites, and pre-seeded report favorites.
   */
  REPORTS: {
    key: "reports",
    name: "Reports",
    description: "Enable conversational reports in Ask Coffee and the Reports tab in Favorites",
    defaultValue: false,
  },

  /**
   * Quotas
   * Enables the Quota tab inside the Deals page (admin-editable per-person quotas
   * by period, with manager rollups and attainment vs Closed Won).
   */
  QUOTAS: {
    key: "quotas",
    name: "Quotas",
    description: "Enable the Quota tab in Deals for admin-editable per-person quotas by period",
    defaultValue: false,
  },

  /**
   * CRM Sync Objects Mapping
   * Object types with implemented inbound (CRM→Coffee) sync — Notes, Tasks, Meetings, Emails —
   * ship REGARDLESS of this flag; their toggles persist via `syncForwardEntityTypes`. This flag
   * only gates the object types whose sync is NOT yet implemented (Calls/Files/Tickets/Products),
   * which render disabled ("Coming soon") when it's on. See docs/patterns/48-crm-sync-wizard.md.
   */
  CRM_SYNC_OBJECTS_MAPPING: {
    key: "crm-sync-objects-mapping",
    name: "CRM Sync Objects Mapping",
    description: "Reveal the not-yet-implemented CRM sync object types (Calls/Files/Tickets/Products) as disabled \"Coming soon\" rows. Notes/Tasks/Meetings/Emails ship regardless.",
    defaultValue: false,
  },

  /**
   * Field History
   * Enables the "View History" option in the cell context menu, showing prior values
   * for a field along with who changed them and when.
   */
  USE_OPENSEARCH_INDEX_LEAD_FINDER: {
    key: "use_opensearch_index_lead_finder",
    name: "OpenSearch Lead Finder",
    description: "Route lead finder reads through OpenSearch instead of PostgreSQL",
    defaultValue: true,
  },

  FIELD_HISTORY: {
    key: "field-history",
    name: "Field History",
    description: "Enable View History in the cell context menu to see prior field values",
    defaultValue: false,
  },

  /**
   * Products
   * Enables the Products workspace section (catalog of products/services that can be
   * associated with deals) and Products mapping in CRM sync wizards.
   */
  PRODUCTS: {
    key: "products",
    name: "Products",
    description: "Enable the Products workspace section and Products mapping in CRM sync wizards",
    defaultValue: false,
  },
  /**
   * Intelligence extra knowledge areas
   * Enables knowledge areas not yet persisted by the backend (Products & Services,
   * Sales Process, custom knowledge items). Hidden until backend support lands.
   */
  INTELLIGENCE_EXTRA_KNOWLEDGE: {
    key: "intelligence-extra-knowledge",
    name: "Intelligence Extra Knowledge",
    description: "Enable knowledge areas not yet backed by the workspace intelligence API",
    defaultValue: false,
  },

  BEST_CONTACT_PERSON: {
    key: "best-contact-person",
    name: "Best Contact Person",
    description: "Expose the BCP toggle on Visitor ID settings. Backend (visitor-id-service) reads the same flag key from workspace_preferences before processing.",
    defaultValue: true,
  },

  /**
   * RB2B OEM domain registration
   * Replaces the informational Website URL field with the OEM domain-
   * registration gate (Activate tracking → /webhook-config/:id/register-domain
   * → persist + "Tracking <domain> ✓"). Backend reads the same key from
   * workspace_preferences before serving the OEM-enabled pixel.
   */
  RB2B_OEM: {
    key: "rb2b-oem",
    name: "RB2B OEM",
    description: "Enable RB2B OEM domain registration on Visitor ID. Backend reads the same flag key from workspace_preferences before serving the OEM pixel, processing webhooks, and lazy-registering domains.",
    defaultValue: true,
  },

  /**
   * Website forms tracking
   * Exposes the Website Forms section under Tracking settings.
   */
  TRACKING_WEBSITE_FORMS: {
    key: "tracking-website-forms",
    name: "Tracking: Website Forms",
    description: "Expose the Website Forms section under Tracking settings",
    defaultValue: true,
  },

  /**
   * Product usage tracking
   * Exposes the Product Usage section under Tracking settings.
   */
  TRACKING_PRODUCT_USAGE: {
    key: "tracking-product-usage",
    name: "Tracking: Product Usage",
    description: "Expose the Product Usage section under Tracking settings",
    defaultValue: false,
  },

  /**
   * Deal Score
   * Exposes the fabricated Deal Score column, kanban badge, and card-fields toggle.
   */
  DEAL_SCORE: {
    key: "deal-score",
    name: "Deal Score",
    description: "Expose the Deal Score column on the Deals grid and kanban card",
    defaultValue: false,
  },

  /**
   * Forecast Category
   * Exposes the Forecast Category column and Best Case / Commit rollup tiles
   * on the Deals page.
   */
  FORECAST_CATEGORY: {
    key: "forecast-category",
    name: "Forecast Category",
    description: "Expose the Forecast Category column and Best Case / Commit rollups on Deals",
    defaultValue: false,
  },

  /**
   * Read-only seats
   * Exposes the "Read only" role option in invite flows (sidebar invite page,
   * Settings > Teammates, setup wizard). Workspaces without this flag don't
   * see the option and can only assign owner/admin/member.
   */
  READ_ONLY_SEATS: {
    key: "read-only-seats",
    name: "Read-only Seats",
    description: "Expose the Read only role option in invite flows (sidebar invite, Settings > Teammates, setup wizard)",
    defaultValue: false,
  },

  /**
   * Read-only meetings
   * Lets read_only users use meeting capture: connect email/calendar, configure
   * the recorder, download the desktop app, toggle meeting recording, and share
   * recordings/clips. Off = read-only users are blocked from all of those.
   * The backend enforces the same preferences key server-side.
   */
  READ_ONLY_MEETINGS: {
    key: "read-only-meetings",
    name: "Read-only Meetings",
    description: "Let read-only users connect email/calendar, record meetings, and share recordings/clips",
    defaultValue: false,
  },

  /**
   * Editable meeting summary + AI notes
   * Persists edits made in the meeting sheet's Summary tab (updateEngagementSummary)
   * and the Notes tab's AI Notes editor (updateNote.aiNotesContent) with the same
   * debounced auto-save as Manual Notes. Off = those editors stay as today
   * (typing is never saved). Requires the matching coffee backend mutation.
   */
  MEETING_SUMMARY_EDIT: {
    key: "meeting-summary-edit",
    name: "Editable Meeting Summary",
    description: "Save edits to the meeting Summary and AI Notes in the meeting sheet",
    defaultValue: false,
  },

  /**
   * Deal detail sheet "More" fields section
   * Read-only expansion listing every standard + custom field. UI-only until
   * backend wires up read/write for the additional deal properties.
   */
  DEAL_DETAIL_MORE_FIELDS: {
    key: "deal-detail-more-fields",
    name: "Deal Detail More Fields",
    description: "Show the collapsible More section listing all deal fields in the deal drill-down (UI-only until backend support lands)",
    defaultValue: false,
  },

  /**
   * Company Street address
   * Exposes the Street column on the Companies grid and the Street row in the
   * company detail sheet's "More" section. Backend does not yet persist a
   * street property on Account.
   */
  COMPANY_STREET_ADDRESS: {
    key: "company-street-address",
    name: "Company Street Address",
    description: "Enable Street field/column on Companies (UI-only until backend adds a street property on Account)",
    defaultValue: false,
  },

  /**
   * Workspace Data Model settings section
   * Adds a "Data Model" section under workspace settings that lists the
   * standard fields for People, Companies, Deals, Notes, and Tasks and lets
   * admins add new custom fields per object type. Backed by the same
   * custom-field CRUD as the grid column manager, so it is on by default; the
   * section is still permission-gated on `edit:workspace:settings`, and
   * mutating controls on admin/owner.
   */
  WORKSPACE_DATA_MODEL: {
    key: "workspace-data-model",
    name: "Workspace Data Model",
    description: "Show the Data Model section in workspace settings for reviewing and adding fields per object type",
    defaultValue: true,
  },

  /**
   * Monthly credit add-on
   * Exposes the recurring "Monthly Credit Add-on" selector on the Plans page.
   * Hidden by default: the selection is not yet supported by the checkout
   * backend (CreateCheckoutSessionInput has no add-on field), so it only
   * changes the displayed total without ever billing the add-on.
   */
  MONTHLY_CREDIT_ADDON: {
    key: "monthly-credit-addon",
    name: "Monthly Credit Add-on",
    description: "Expose the recurring monthly credit add-on selector on the Plans page",
    defaultValue: false,
  },

  /**
   * Column editing scope & per-list overrides
   * Gates the "Editing: Just for me / Everyone in workspace" scope selector
   * and the "Use custom columns for this list" toggle in the grid column
   * manager. Hidden by default: the layering is localStorage-only — the
   * backend has no shared column-layout persistence yet — so "Everyone in
   * workspace" never actually reaches anyone else's browser. With the flag
   * off, grids read/write the original single storage key per entity.
   */
  COLUMN_EDITING_SCOPE: {
    key: "column-editing-scope",
    name: "Column Editing Scope",
    description: "Show the editing-scope selector and per-list column override toggle in the grid column manager (localStorage-only until backend support lands)",
    defaultValue: false,
  },

  /**
   * Record Sharing
   * Swaps the Privacy section's visibility block for the consolidated ACL
   * scale (Email & Recordings Visibility incl. manager tiers + user groups)
   * and enables the per-record share dialog for engagements, files, and
   * notes. Entry points additionally require the workspace's
   * recordSharingEnabled pref.
   */
  RECORD_SHARING: {
    key: "record-sharing",
    name: "Record Sharing",
    description: "Enable the consolidated Privacy visibility scale, user groups, and per-record group sharing",
    defaultValue: false,
  },

  /**
   * Job-change suggestions (SC-13586)
   * The person-sheet banner for an open "may have changed job" question. The review dialog
   * itself also opens from a bell deep link regardless of this flag — a notification only
   * exists for a workspace the backend opted in (job_change_notifications_enabled).
   */
  CONTACT_JOB_CHANGE_SUGGESTIONS: {
    key: "contact-job-change-suggestions",
    name: "Job change suggestions",
    description: "Show the job-change review banner on person sheets",
    defaultValue: false,
  },

  /**
   * "Not in the last N" relative-date mode for Last Email
   * Adds an "In the last / Not in the last" mode selector to the Last Email
   * filter on People, Companies and Deals. "Not in the last N days" matches
   * records whose last email is older than the window OR who were never
   * emailed (lastEmail filter with notWithinLast). Only columns that opt in
   * via FilterColumn.allowNotInLast show the selector.
   */
  LAST_EMAIL_NOT_IN_LAST: {
    key: "last-email-not-in-last",
    name: "Last Email: Not in the last",
    description: "Offer a \"Not in the last N days\" mode (includes never-emailed records) on the Last Email filter",
    defaultValue: false,
  },

  /**
   * Calls tab on Person / Company / Deal detail sheets
   * Lists CALL engagements, which only exist where HubSpot call sync-forward
   * is on (workspace pref sync_forward_entity_types.CALL, coffee#4959). The
   * flag hides the tab and skips its query; initially on for SBTX (ws 4130) only.
   */
  CALLS_TAB: {
    key: "calls-tab",
    name: "Calls tab",
    description: "Show a Calls tab (HubSpot-synced calls) next to Emails on person, company and deal sheets",
    defaultValue: false,
  },
} as const;

/**
 * Get a feature flag by key
 * Returns undefined if the feature doesn't exist
 */
export const getFeatureFlag = (key: string) => {
  return Object.values(FEATURE_FLAGS).find((flag) => flag.key === key);
};

/**
 * Get all feature flags
 */
export const getAllFeatureFlags = () => {
  return Object.values(FEATURE_FLAGS);
};
